import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { mfgAPI } from '../../services/api';
import { toast } from 'sonner';
import {
  Factory, Cog, Layers, GitBranch, ClipboardList, Plus, ArrowRight,
  Boxes, Activity, CheckCircle2,
} from 'lucide-react';

const STATUS_COLORS = {
  CREATED: 'bg-slate-100 text-slate-700',
  RELEASED: 'bg-orange-100 text-orange-700',
  PARTIALLY_CONFIRMED: 'bg-amber-100 text-amber-700',
  CONFIRMED: 'bg-blue-100 text-blue-700',
  TECO: 'bg-emerald-100 text-emerald-700',
  CLOSED: 'bg-gray-200 text-gray-600',
};

const ManufacturingDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      const res = await mfgAPI.getDashboard();
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load manufacturing dashboard');
    } finally {
      setLoading(false);
    }
  };

  const stats = [
    { label: 'Production Orders', value: data?.total_orders ?? 0, icon: ClipboardList, color: 'orange', path: '/manufacturing/production-orders' },
    { label: 'Open Orders', value: data?.open_orders ?? 0, icon: Activity, color: 'amber', path: '/manufacturing/production-orders' },
    { label: 'Work Centers', value: data?.total_work_centers ?? 0, icon: Cog, color: 'blue', path: '/manufacturing/work-centers' },
    { label: 'Bills of Material', value: data?.total_boms ?? 0, icon: Layers, color: 'emerald', path: '/manufacturing/boms' },
  ];

  const tiles = [
    { label: 'Work Centers', desc: 'Capacity & cost-center resources', icon: Cog, path: '/manufacturing/work-centers' },
    { label: 'Bills of Material', desc: 'Product structures & components', icon: Layers, path: '/manufacturing/boms' },
    { label: 'Routings', desc: 'Operation sequences', icon: GitBranch, path: '/manufacturing/routings' },
    { label: 'Production Orders', desc: 'Shop-floor execution', icon: ClipboardList, path: '/manufacturing/production-orders' },
    { label: 'Number Ranges', desc: 'Document numbering config', icon: Boxes, path: '/manufacturing/number-ranges' },
  ];

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="manufacturing-dashboard">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-orange-600 rounded-xl">
              <Factory className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Manufacturing (PP)</h1>
              <p className="text-sm text-slate-500">Production planning &amp; shop-floor control</p>
            </div>
          </div>
          <Button onClick={() => navigate('/manufacturing/production-orders/create')} className="bg-orange-600 hover:bg-orange-700" data-testid="dash-create-order-btn">
            <Plus className="h-4 w-4 mr-2" /> New Production Order
          </Button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((s) => (
            <Card key={s.label} className="border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate(s.path)} data-testid={`stat-${s.label.replace(/\s+/g, '-').toLowerCase()}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500">{s.label}</p>
                    <p className="text-3xl font-bold text-slate-900 mt-1">{loading ? '—' : s.value}</p>
                  </div>
                  <div className={`p-3 rounded-xl bg-${s.color}-100`}>
                    <s.icon className={`h-5 w-5 text-${s.color}-600`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-3">
            <h2 className="text-base font-semibold text-slate-800">Quick Access</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {tiles.map((t) => (
                <Card key={t.label} className="border border-slate-200 shadow-none cursor-pointer hover:border-orange-300 hover:bg-orange-50/40 transition-colors group" onClick={() => navigate(t.path)} data-testid={`tile-${t.label.replace(/\s+/g, '-').toLowerCase()}`}>
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="p-2.5 rounded-lg bg-slate-100 group-hover:bg-orange-100">
                      <t.icon className="h-5 w-5 text-slate-600 group-hover:text-orange-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">{t.label}</p>
                      <p className="text-xs text-slate-500">{t.desc}</p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-orange-500" />
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <h2 className="text-base font-semibold text-slate-800">Recent Orders</h2>
            <Card className="border border-slate-200 shadow-none">
              <CardContent className="p-2">
                {loading ? (
                  <p className="text-sm text-slate-400 p-4">Loading…</p>
                ) : (data?.recent_orders?.length ? data.recent_orders.map((o) => (
                  <div key={o.order_id} className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/manufacturing/production-orders/${o.order_id}`)}>
                    <div className="min-w-0">
                      <p className="font-mono text-sm font-medium text-orange-600">{o.order_id}</p>
                      <p className="text-xs text-slate-500 truncate">{o.material_id} · {o.order_quantity} {o.uom}</p>
                    </div>
                    <Badge className={`${STATUS_COLORS[o.system_status] || 'bg-slate-100'} border-0 text-[10px]`}>{o.system_status}</Badge>
                  </div>
                )) : (
                  <div className="p-6 text-center text-slate-400 text-sm flex flex-col items-center gap-2">
                    <CheckCircle2 className="h-8 w-8 text-slate-200" />
                    No orders yet
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ManufacturingDashboard;
